import { z } from "zod";
import { ONBOARDING_TOUR_IDS, TOUR_CAPABILITIES } from "@playbookai/types";
import type { TourCapability } from "@playbookai/types";

/**
 * Versioned JSON tour definitions, validated against ONE schema.
 *
 * Definitions are trusted repository CODE, not tenant data: there is no
 * admin-authored tour and no remote definition source. What the schema is for
 * is the failure the spec names — a malformed or drifted definition must
 * disable its own tour, never break the product page it was going to run on.
 *
 * Two things a definition deliberately cannot express:
 *
 * - **A selector.** A step names a registered semantic TARGET ID; the renderer
 *   resolves it against the registry the hosts populate. JSON that could carry
 *   a CSS selector would be JSON that could reach any element on the page, and
 *   generated class names and `nth-child` positions are exactly the couplings
 *   the spec forbids.
 * - **Code.** There is no handler, no expression and no navigation. A step
 *   points, explains, and moves on.
 */

/** A registered semantic target, e.g. `shell.new-tab`. */
export const TourTargetIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z][a-z0-9-]*(\.[a-z][a-z0-9-]*)+$/,
    "a target id is dotted lower-kebab, e.g. shell.new-tab",
  );
export type TourTargetId = z.infer<typeof TourTargetIdSchema>;

/**
 * The capability vocabulary lives in `@playbookai/types` and is re-exported
 * here, because THREE packages need the same list: this renderer validates
 * definitions against it, the shell resolves it, and a feature package reports
 * it on an occasion. One list, one home, no drift.
 */
export { TOUR_CAPABILITIES, type TourCapability };

/**
 * What to do when a step's targets are not all on the page.
 *
 * `use-available` is the Home apps step: two targets, and Quick Access may have
 * been removed from the dashboard. It renders against whatever IS present and
 * swaps in `fallbackBody`. `skip` drops the step from the applicable set
 * entirely — which also removes it from the progress count, so a person is
 * never told they are on step 3 of 5 when only 4 can ever appear.
 */
export const MISSING_TARGET_POLICIES = ["use-available", "skip"] as const;
export type MissingTargetPolicy = (typeof MISSING_TARGET_POLICIES)[number];

export const TOUR_PLACEMENTS = [
  "auto",
  "top",
  "bottom",
  "start",
  "end",
  "inside-start",
] as const;
export type TourPlacement = (typeof TOUR_PLACEMENTS)[number];

export const TourStepSchema = z
  .object({
    /** Stable across definition versions — it is what resume is keyed on. */
    id: z
      .string()
      .min(1)
      .max(64)
      .regex(/^[a-z][a-z0-9-]*$/, "a step id is lower-kebab"),
    title: z.string().min(1).max(80),
    body: z.string().min(1).max(400),
    /**
     * The spotlights. Several means one card over several cut-outs.
     *
     * EMPTY means an UNANCHORED informational step: a centred card over a plain
     * scrim, pointing at nothing. That is the honest shape for content that is
     * about a whole screen rather than a control on it — "this is your Hoshin
     * workspace" — and it is deliberately different from a step whose target
     * went missing. A missing target means the thing being explained is not
     * there, so the explanation is dropped; an unanchored step's subject is the
     * screen itself, which is by definition present, so it always applies.
     *
     * Never reach for this to keep a step alive whose CONTROL is absent. Use it
     * only when the step was never about a control.
     */
    targets: z.array(TourTargetIdSchema).max(4).default([]),
    /** Card anchor may differ from the highlighted section. */
    anchor: TourTargetIdSchema.optional(),
    /** Alternative section to explain if the primary target is unavailable. */
    fallbackTargets: z.array(TourTargetIdSchema).max(4).optional(),
    placement: z.enum(TOUR_PLACEMENTS).default("auto"),
    /** False preserves the page scroll position throughout this step. */
    scrollIntoView: z.boolean().optional(),
    missingTargetPolicy: z.enum(MISSING_TARGET_POLICIES).default("skip"),
    /** Used when `use-available` drops one of several targets. */
    fallbackBody: z.string().min(1).max(400).optional(),
    /** Every listed capability must be present for the step to apply. */
    requires: z.array(z.enum(TOUR_CAPABILITIES)).max(4).default([]),
    /**
     * Every listed capability must be ABSENT for the step to apply.
     *
     * This is what lets one idea have two honest phrasings — "add your
     * knowledge, your changes are shared" for somebody who can edit, and
     * "review the team's work" for somebody who cannot — as a pair of mutually
     * exclusive steps rather than one step whose copy promises an affordance
     * the reader does not have. Exactly one of the pair is ever applicable, so
     * the progress count stays right.
     */
    forbids: z.array(z.enum(TOUR_CAPABILITIES)).max(4).default([]),
  })
  .strict()
  .refine(
    (step) =>
      step.missingTargetPolicy !== "use-available" ||
      step.targets.length > 1 ||
      step.fallbackBody !== undefined,
    {
      message:
        "use-available on a single-target step needs a fallbackBody, or it has nothing to fall back to",
    },
  )
  .refine(
    (step) =>
      step.targets.length > 0 || step.missingTargetPolicy !== "use-available",
    {
      message:
        "an unanchored step has no targets to fall back FROM; drop missingTargetPolicy",
    },
  )
  .refine(
    (step) => step.targets.length > 0 || step.fallbackBody === undefined,
    {
      message: "an unanchored step's fallbackBody would never be reachable",
    },
  );
export type TourStep = z.infer<typeof TourStepSchema>;

export const TourDefinitionSchema = z
  .object({
    id: z.enum(ONBOARDING_TOUR_IDS),
    version: z.number().int().min(1),
    label: z.string().min(1).max(100).optional(),
    steps: z.array(TourStepSchema).min(1).max(12),
  })
  .strict()
  .refine(
    (tour) =>
      new Set(tour.steps.map((step) => step.id)).size === tour.steps.length,
    { message: "step ids must be unique within a tour" },
  );
export type TourDefinition = z.infer<typeof TourDefinitionSchema>;

export interface TourDefinitionSet {
  readonly tours: ReadonlyMap<string, TourDefinition>;
  /** One line per rejected definition, for a boot-time log or a spec. */
  readonly rejected: readonly string[];
}

/**
 * Validate a set of definitions, keeping the good ones.
 *
 * Per-tour isolation is the whole behaviour: a malformed `vsm` definition must
 * cost the VSM tour and nothing else. Returning a partial set rather than
 * throwing is what keeps a definition mistake from being a blank product page.
 */
export function loadTourDefinitions(
  raw: readonly unknown[],
): TourDefinitionSet {
  const tours = new Map<string, TourDefinition>();
  const rejected: string[] = [];
  for (const candidate of raw) {
    const parsed = TourDefinitionSchema.safeParse(candidate);
    if (!parsed.success) {
      const id =
        typeof candidate === "object" &&
        candidate !== null &&
        typeof (candidate as { id?: unknown }).id === "string"
          ? (candidate as { id: string }).id
          : "<unidentified>";
      rejected.push(
        `${id}: ${parsed.error.issues.map((i) => i.message).join("; ")}`,
      );
      continue;
    }
    if (tours.has(parsed.data.id)) {
      rejected.push(`${parsed.data.id}: duplicate definition`);
      continue;
    }
    tours.set(parsed.data.id, parsed.data);
  }
  return { tours, rejected };
}

export interface ApplicableStepContext {
  /** Target ids currently registered AND mounted. */
  readonly availableTargets: ReadonlySet<string>;
  readonly capabilities: ReadonlySet<TourCapability>;
}

export interface ApplicableStep {
  readonly step: TourStep;
  /** The subset of `step.targets` actually on the page, in declared order. */
  readonly targets: readonly string[];
  /** `fallbackBody` when a target was dropped, else `step.body`. */
  readonly body: string;
}

/**
 * The steps this person, on this page, can actually be shown.
 *
 * Progress is counted against THIS list rather than the definition, so "2 of 4"
 * never counts a step that was never going to appear. A tour whose applicable
 * list is empty does not run at all — it exits without claiming the person saw
 * content that was not there.
 */
export function applicableSteps(
  definition: TourDefinition,
  context: ApplicableStepContext,
): readonly ApplicableStep[] {
  const applicable: ApplicableStep[] = [];
  for (const step of definition.steps) {
    if (
      step.requires.some((capability) => !context.capabilities.has(capability))
    ) {
      continue;
    }
    if (
      step.forbids.some((capability) => context.capabilities.has(capability))
    ) {
      continue;
    }
    // An UNANCHORED step is about the screen, not a control on it, so there is
    // nothing that could be missing. It renders centred over a plain scrim.
    if (step.targets.length === 0) {
      applicable.push({ step, targets: [], body: step.body });
      continue;
    }
    const present = step.targets.filter((target) =>
      context.availableTargets.has(target),
    );
    if (present.length === 0) {
      const fallback = (step.fallbackTargets ?? []).filter((target) =>
        context.availableTargets.has(target),
      );
      if (fallback.length > 0)
        applicable.push({
          step,
          targets: fallback,
          body: step.fallbackBody ?? step.body,
        });
      continue;
    }
    if (present.length < step.targets.length) {
      if (step.missingTargetPolicy === "skip") continue;
      applicable.push({
        step,
        targets: present,
        body: step.fallbackBody ?? step.body,
      });
      continue;
    }
    applicable.push({ step, targets: present, body: step.body });
  }
  return applicable;
}

/**
 * Where to resume.
 *
 * A saved step id that the current definition no longer contains — or that is
 * no longer applicable here — resolves to the FIRST applicable step rather
 * than to nothing. Updating a definition must not strand somebody mid-tour,
 * and it must not reset a completed one either (completion is checked before
 * this function is ever reached).
 */
export function resumeIndex(
  steps: readonly ApplicableStep[],
  lastStepId: string | null | undefined,
): number {
  if (!lastStepId) return 0;
  const index = steps.findIndex((entry) => entry.step.id === lastStepId);
  return index < 0 ? 0 : index;
}
